import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { mixpanel } from '@/mixpanel'
import { useHealthParameterUpdate } from '@/modules/profile/state/useHealthParameterUpdate'
import { BadgeService } from '@/modules/shared/services/badgeService'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isIntramuros } from '@/utils/intramuros'
import { getAnswerableQuestionsAndPrune } from '../utils/questionnaire-engine.utils'
import { validateQuestionnaireAnswers } from '../utils/questionnaire-evaluation.utils'
import { questionnaireEngineReducer } from './questionnaireEngine.reducer'

import type { QuestionAnswerInput } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters } from 'index'
import type { QuestionnaireCareplanActivityWithIncludes } from '../types/questionnaire.types'
import type { QuestionnaireEngineContextType } from '../types/questionnaire-engine.types'

const initialState: QuestionnaireEngineContextType = {
  state: {
    mode: 'new',
    status: 'loading',
    questionnaire: {
      item: [],
    } as unknown as IQuestionnaireWithHealthParameters,
    answerableQuestions: [],
    answers: {},
    validations: {},
    navigationHistory: [],
    historyCursor: 0,
    initialDeeplinkQuestion: undefined,
  },
  navigate: () => {},
  resetToLinkId: () => {},
  goBack: () => {},
  setAnswer: () => {},
  saveProgress: () => Promise.resolve(),
  submitQuestionnaire: () => Promise.resolve(),
}

export const QuestionnaireEngineContext = createContext<QuestionnaireEngineContextType>(initialState)

/**
 * QuestionnaireEngineProvider
 *
 * React context provider responsible for the full lifecycle and state machine
 * of a questionnaire (new, edit, reconfirm, read-only).
 *
 * Responsibilities:
 *  - Initializes questionnaire state from a careplan activity
 *  - Computes which questions are answerable based on skip logic / conditions
 *  - Stores all answers in a normalized QuestionAnswerInput structure
 *  - Manages navigation (next/previous/goToLinkId) with mode-specific behavior:
 *    - 'reconfirm' mode: Smart navigation to invalid questions first, uses navigation history for back
 *    - Other modes: Sequential navigation through answerable questions
 *  - Handles conditional visibility changes (visibility diff + pending clear)
 *  - Determines questionnaire mode: 'new' | 'edit' | 'reconfirm' | 'readOnly'
 *  - Provides API methods for saving progress (incremental) and completing (final submission)
 *
 * Props:
 * @param {Object} activity — CareplanActivity with linked questionnaire + response
 * @param {ReactNode} children — Components that require access to the engine
 *
 * Usage:
 * <QuestionnaireEngineProvider activity={activity}>
 *   <QuestionnairePage />
 * </QuestionnaireEngineProvider>
 *
 * Exposed state includes:
 *  - answers: Current user answers
 *  - answerableQuestions: Only the visible & active questions after skip logic
 *  - currentQuestion, currentIndex
 *  - totalQuestions, canGoNext, canGoBack
 *  - mode and status
 *
 * Exposed actions include:
 *  - setAnswer(answer): Updates local state, recalculates skip logic & validations. Does NOT persist to API.
 *  - goNext(), goBack(), goToLinkId(linkId): Navigation methods
 *    - In 'reconfirm' mode: goNext() jumps to next invalid question; goBack() uses navigation history
 *  - saveProgress(overrideAnswers?): Persists VALID answers to backend (status: 'in-progress' or 'amended'). Call this before navigation.
 *  - submitQuestionnaire(overrideAnswers?): Marks questionnaire as 'completed' and persists final answers.
 */
export const QuestionnaireEngineProvider = ({
  children,
  activity,
  initialLinkId,
}: {
  activity: QuestionnaireCareplanActivityWithIncludes
  children: React.ReactNode
  initialLinkId?: string
}) => {
  // State
  // ------------------------------------------------------------------------- /
  const [state, dispatch] = useReducer(questionnaireEngineReducer, initialState.state)

  // Hooks
  // ------------------------------------------------------------------------- /
  const subject = useSubjectMember()
  const axios = useAxios()
  const { setHealthParameterUpdateState } = useHealthParameterUpdate()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  // Effects
  // ------------------------------------------------------------------------- /
  const activityRef = useRef<string | number | null>(null)

  useEffect(() => {
    // If subject is not available, do nothing
    if (!subject) return

    // If activity is the same, do nothing
    if (activityRef.current === activity.id) return
    activityRef.current = activity.id

    dispatch({
      type: 'INITIALIZE',
      payload: { activity, subject, initialLinkId },
    })
  }, [activity.id, initialLinkId, subject])

  // Methods
  // ------------------------------------------------------------------------- /

  const navigate = useCallback((linkId: string, behavior: 'push' | 'replace') => {
    dispatch({
      type: 'NAVIGATE',
      payload: { linkId, behavior },
    })
  }, [])

  const goBack = useCallback(() => {
    dispatch({
      type: 'GO_BACK',
    })
  }, [])

  const resetToLinkId = useCallback((linkId: string) => {
    dispatch({
      type: 'RESET_TO_LINK_ID',
      payload: { linkId },
    })
  }, [])

  const setAnswer = useCallback(
    (answer: QuestionAnswerInput & { fileIds: number[] }) => {
      if (!subject) return

      dispatch({
        type: 'SET_ANSWER',
        payload: { answer, subject },
      })
    },
    [subject],
  )

  /**
   * saveProgress
   *
   * Persists current answers to the backend incrementally.
   * Called after each answer change to save progress.
   */
  const saveProgress = useCallback(
    async (answersToSave: { [linkId: string]: QuestionAnswerInput }) => {
      if (!subject) {
        return
      }

      const answersToPost = Object.values(answersToSave)

      await axios.post(`/careplan-activity/${activity.id}/questionnaire-response`, {
        questionnaireId: activity.questionnaire.id,
        answers: answersToPost,
        status: state.mode === 'reconfirm' ? 'amended' : 'in-progress',
        transitionCheck: isIntramuros ? 'ignore' : undefined,
      })
    },
    [activity.id, activity.questionnaire.id, axios, state.mode, subject],
  )

  const submitQuestionnaire = useCallback(
    async (answersToSave: { [linkId: string]: QuestionAnswerInput }) => {
      if (!subject) {
        return
      }

      // Recalculate answerableQuestions from the answers being submitted
      // to avoid stale state when setAnswer + autoAdvance triggers submission
      // before React re-renders with updated state.
      const { answerableQuestions: freshAnswerableQuestions } = getAnswerableQuestionsAndPrune(
        activity.questionnaire,
        answersToSave,
        subject,
      )

      const answersValidations = validateQuestionnaireAnswers(freshAnswerableQuestions, answersToSave, subject)

      const canSubmit = Object.values(answersValidations).every((validation) => validation.isValid)

      if (!canSubmit) {
        // Navigate to the first invalid question (in questionnaire order)
        // so the user can fix it
        for (const q of freshAnswerableQuestions) {
          const linkId = q.linkId || q.id || ''
          if (linkId && !answersValidations[linkId]?.isValid) {
            resetToLinkId(linkId)
            break
          }
        }
        throw new Error('Questionnaire is not valid')
      }
      const answersToSubmit = Object.values(answersToSave)

      await axios.post(`/careplan-activity/${activity.id}/questionnaire-response`, {
        questionnaireId: activity.questionnaire.id,
        answers: answersToSubmit,
        status: 'completed',
        transitionCheck: isIntramuros ? 'ignore' : undefined,
      })

      mixpanel.track('questionnaire_submit', {
        questionnaire_slug: activity.questionnaire.name,
        is_mandate: isLoggedInForMandatee,
        is_intramuros: isIntramuros,
      })

      // Update badge count since activity was completed. Excludes repeating
      if (!activity.meta.repeating) {
        BadgeService.decrementBadgeCount()
      }

      if (activity.questionnaire.type === 'observation') {
        // Add entry to local storage
        setHealthParameterUpdateState({
          member: subject?.id,
          slug: activity.questionnaire.name,
          timestamp: Date.now(),
        })
      }
    },
    [activity.id, activity.questionnaire.id, axios, subject],
  )

  /**
   * resolveNextAnswerState
   *
   * Calculates the future state of the questionnaire (validity, completeness)
   * based on a candidate answer, without modifying the actual state.
   *
   * Useful for "Auto-Advance" logic where we need to know if applying an answer
   * would make the questionnaire complete (to trigger submission) or just valid
   * (to trigger navigation).
   */
  const resolveNextAnswerState = useCallback(
    (answer: QuestionAnswerInput) => {
      if (!subject) return { isComplete: false, isValid: false }

      const nextAnswers = {
        ...state.answers,
        [answer.linkId]: answer,
      }

      const { answerableQuestions } = getAnswerableQuestionsAndPrune(state.questionnaire, nextAnswers, subject)

      const validations = validateQuestionnaireAnswers(answerableQuestions, nextAnswers, subject)

      const isNowValid = Object.values(validations).every((v: { isValid: boolean }) => v.isValid)

      return {
        isComplete: isNowValid,
        isValid: isNowValid,
      }
    },
    [state.answers, state.questionnaire, subject],
  )

  const contextValue = useMemo(
    () => ({
      state,
      navigate,
      goBack,
      resetToLinkId,
      setAnswer,
      saveProgress,
      submitQuestionnaire,
      resolveNextAnswerState,
    }),
    [state, navigate, goBack, resetToLinkId, setAnswer, saveProgress, submitQuestionnaire, resolveNextAnswerState],
  )

  return <QuestionnaireEngineContext.Provider value={contextValue}>{children}</QuestionnaireEngineContext.Provider>
}
